const AVAILABILITY_FACET = "availability";
const ARCHIVED_EXCLUSION = "NOT onlyArchivedCourseRuns:true";

type SearchRequest = {
  indexName?: string;
  params?: Record<string, unknown>;
};

type FacetCounts = Record<string, number>;

type SearchResponse = {
  facets?: Record<string, FacetCounts>;
  [key: string]: unknown;
};

type MultiSearchResponse = {
  results?: SearchResponse[];
};

export type AvailabilityFacetMerge = {
  sourceIndex: number;
  extraIndex: number;
};

export type AvailabilityFacetPlan = {
  requests: SearchRequest[];
  merges: AvailabilityFacetMerge[];
  originalCount: number;
};

const filtersOf = (request: SearchRequest): string =>
  typeof request.params?.filters === "string" ? request.params.filters : "";

const excludesOnlyArchivedCourseRuns = (request: SearchRequest): boolean =>
  filtersOf(request).includes(ARCHIVED_EXCLUSION);

const stripOnlyArchivedCourseRunsExclusion = (filters: string): string =>
  filters
    .replace(/\s+AND\s+NOT onlyArchivedCourseRuns:true/g, "")
    .replace(/^NOT onlyArchivedCourseRuns:true(?:\s+AND\s+)?/, "")
    .trim();

const facetNames = (facets: unknown): string[] => {
  if (typeof facets === "string") {
    return facets
      .split(",")
      .map((facet) => facet.trim())
      .filter(Boolean);
  }
  if (Array.isArray(facets)) {
    return facets.filter((facet): facet is string => typeof facet === "string");
  }
  return [];
};

const requestsAvailabilityFacet = (request: SearchRequest): boolean => {
  const names = facetNames(request.params?.facets);
  return names.includes("*") || names.includes(AVAILABILITY_FACET);
};

const isAvailabilityCountRequest = (request: SearchRequest): boolean => {
  const names = facetNames(request.params?.facets);
  return (
    request.params?.hitsPerPage === 0 &&
    names.length === 1 &&
    names[0] === AVAILABILITY_FACET
  );
};

const withoutOnlyArchivedCourseRunsExclusion = (
  request: SearchRequest,
): SearchRequest => ({
  ...request,
  params: {
    ...request.params,
    filters: stripOnlyArchivedCourseRunsExclusion(filtersOf(request)),
  },
});

const availabilityCountRequest = (request: SearchRequest): SearchRequest => ({
  ...request,
  params: {
    ...request.params,
    filters: stripOnlyArchivedCourseRunsExclusion(filtersOf(request)),
    hitsPerPage: 0,
    page: 0,
    facets: [AVAILABILITY_FACET],
    analytics: false,
    clickAnalytics: false,
  },
});

/**
 * Availability counts have to ignore the only-archived exclusion, while the
 * hits query keeps it. InstantSearch sends a separate availability count
 * request only after that facet is refined, so an unrefined dropdown needs
 * one added here.
 */
export const planAvailabilityFacetRequests = (
  requests: SearchRequest[],
): AvailabilityFacetPlan => {
  const planned = requests.map((request) =>
    isAvailabilityCountRequest(request) &&
    excludesOnlyArchivedCourseRuns(request)
      ? withoutOnlyArchivedCourseRunsExclusion(request)
      : request,
  );
  const hasAvailabilityCountRequest = planned.some(isAvailabilityCountRequest);
  const merges: AvailabilityFacetMerge[] = [];

  if (!hasAvailabilityCountRequest) {
    planned.forEach((request, sourceIndex) => {
      if (
        !excludesOnlyArchivedCourseRuns(request) ||
        !requestsAvailabilityFacet(request) ||
        isAvailabilityCountRequest(request)
      ) {
        return;
      }
      merges.push({
        sourceIndex,
        extraIndex: planned.length,
      });
      planned.push(availabilityCountRequest(request));
    });
  }

  return {
    requests: planned,
    merges,
    originalCount: requests.length,
  };
};

export const applyAvailabilityFacetCounts = <T extends MultiSearchResponse>(
  response: T,
  plan: AvailabilityFacetPlan,
): T => {
  if (!response.results || plan.merges.length === 0) {
    return response;
  }

  const results = response.results.map((result) => ({
    ...result,
    facets: { ...result.facets },
  }));

  plan.merges.forEach(({ sourceIndex, extraIndex }) => {
    const source = results[sourceIndex];
    const extra = response.results?.[extraIndex];
    if (!source || !extra?.facets) {
      return;
    }
    source.facets = {
      ...source.facets,
      [AVAILABILITY_FACET]: extra.facets[AVAILABILITY_FACET] ?? {},
    };
  });

  return {
    ...response,
    results: results.slice(0, plan.originalCount),
  };
};
